import { FC, lazy, Suspense, useState } from 'react';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import retry from '../../utils/webpack/chunkRetry';
import MediaManager from '../../MediaPopup/MediaManager';
import Flex from '@wix/da-ds/pkg/Flex';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import { MediaSourceType } from '../../MediaPopup';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { DeviationResourceType } from '../../types/deviation';
import { BiData, TierAddContentBiEvent } from '@wix/da-bi/pkg/events';

const LoadableInit = lazy(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "tiercreator" */
        '../redux/creator/ModuleLoadable'
      )
  )
);

export interface Props {
  type: 'deviations' | 'posts';
  tierDeviation: PapiDeviation;
  profileOwner: PapiUser;
  className?: string;
  addMediaToTier?: (deviations: PapiDeviation[]) => void;
  pushLoadingModal: () => void;
}

const TierManageContentButton: FC<Props> = ({
  type,
  tierDeviation,
  profileOwner,
  className,
  ...props
}) => {
  const { t } = useTranslation();
  const [showMM, setShowMM] = useState(false);

  const tierItemsCount = tierDeviation?.tier?.stats?.[type] || 0;

  const onManagementDone = () => {
    setShowMM(false);

    props.pushLoadingModal();

    // Pressing ESC key to close the MM cancels the reload.
    // Let's postpone it by 1 cycle
    setTimeout(() => {
      window.location.href = Url.userSubscriptionsPath({
        username: profileOwner.username,
        tierDeviationId: tierDeviation.deviationId,
        tierContentType: type,
      });
    }, 1);
  };

  return (
    <ErrorBoundary debugComponent="TierManageContentButton">
      <Flex className={className}>
        <GhostButton
          size={'medium'}
          variant={'main'}
          onClick={() => setShowMM(true)}
          biData={BiData<TierAddContentBiEvent>({
            evid: 532,
            typeid: DeviationResourceType.TIER,
            itemid: tierDeviation.deviationId,
            button_name: type,
          })}
        >
          {type === 'deviations'
            ? t(
                tierItemsCount > 0
                  ? 'deviation.tier.editContent.deviations'
                  : 'deviation.tier.addContent.deviations'
              )
            : t(
                tierItemsCount > 0
                  ? 'deviation.tier.editContent.posts'
                  : 'deviation.tier.addContent.posts'
              )}
        </GhostButton>
        {showMM && (
          <Suspense fallback={null}>
            <LoadableInit />
            <MediaManager
              title={t(
                type === 'deviations'
                  ? 'mediapopup.tierManager.deviations'
                  : 'mediapopup.tierManager.posts'
              )}
              allowUploads={false}
              tier={tierDeviation}
              withAddDeviationsPopupOpen={tierItemsCount === 0}
              onClose={onManagementDone}
              onDone={onManagementDone}
              initialMediaSource={
                type === 'deviations' ? MediaSourceType.TIER_DEVIATIONS : MediaSourceType.TIER_POSTS
              }
            />
          </Suspense>
        )}
      </Flex>
    </ErrorBoundary>
  );
};
TierManageContentButton.displayName = 'TierManageContentButton';

export default TierManageContentButton;
